import { useMemo } from 'react';

import type { SortBy } from '@/components/molecules/SortByDropdown';
import {
    type ClosedFishGroupRow,
    cagesByStatus,
    type OverviewCage,
    type OverviewCageBuckets,
} from '@/domain/client';
import { sortCages } from '@/utils/sortCages';

import type { LocationOverviewRow } from './types';

export interface LocationOverviewCageBuckets extends OverviewCageBuckets<OverviewCage> {
    /**
     * Cohorts that have finished in one of these cages. Already ordered newest
     * departure first by the API, and deliberately not passed through `sortCages`:
     * the sort selectors read cage measurements, which a departed cohort has none
     * of, so every row would compare equal and the API's order would be lost.
     */
    closedFishGroups?: ClosedFishGroupRow[];
}

/**
 * Sorts each bucket on its own and concatenates them in display order: the pens in
 * `OVERVIEW_CAGE_STATUSES` order (active, inactive, empty, historical), then departed
 * cohorts. This is the only place row order lives, so `LocationOverviewTable` never
 * reorders what it is given.
 */
export function useLocationOverviewRows(
    buckets: LocationOverviewCageBuckets,
    sortBy: SortBy
): LocationOverviewRow[] {
    const { activeCages, inactiveCages, emptyCages, historicalCages, closedFishGroups } = buckets;

    return useMemo(
        () => [
            ...cagesByStatus({ activeCages, inactiveCages, emptyCages, historicalCages }).flatMap(
                ({ status, cages }) =>
                    sortCages(cages, sortBy).map((cage): LocationOverviewRow => ({ cage, status }))
            ),
            ...(closedFishGroups ?? []).map(
                (closedFishGroup): LocationOverviewRow => ({
                    closedFishGroup,
                    status: 'closedFishGroup',
                })
            ),
        ],
        [activeCages, inactiveCages, emptyCages, historicalCages, closedFishGroups, sortBy]
    );
}
